
import { useEffect } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

const CookiePage = () => {
  useEffect(() => {
    // Update page title and meta description for cookie policy page
    document.title = 'Cookie Policy - WOTECH Information Technologies | Cookie Usage';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'WOTECH Cookie Policy - Learn about how we use cookies and tracking technologies on our website. Manage your cookie preferences.');
    }

    // Add structured data for WebPage
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Cookie Policy",
      "description": "WOTECH Cookie Policy and tracking technologies information.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/cookie`,
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Cookie Policy",
            "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/cookie`
          }
        ]
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <Header />

      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-100">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">Cookie Policy</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Learn about how WOTECH uses cookies and similar technologies to enhance your browsing experience and improve our services.
            </p>
            <div className="mt-8 text-sm text-gray-500">
              Last updated: December 2024
            </div>
          </div>
        </section>

        {/* Cookie Policy Content */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="prose prose-lg max-w-none">
              
              {/* Introduction */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">What Are Cookies?</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit a website. They are widely used to make websites work more efficiently and provide information to website owners.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  WOTECH Information Technologies uses cookies and similar tracking technologies to enhance your experience on our website, analyze usage patterns, and improve our services.
                </p>
              </div>

              {/* Types of Cookies */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Types of Cookies We Use</h2>
                
                <div className="space-y-8">
                  <div className="bg-blue-50 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center mr-3">
                        <i className="ri-settings-line text-white text-sm"></i>
                      </div>
                      Essential Cookies
                    </h3>
                    <p className="text-gray-600 leading-relaxed mb-3">
                      These cookies are necessary for the website to function properly. They enable basic functions like page navigation, access to secure areas, and form submissions.
                    </p>
                    <ul className="list-disc pl-6 text-gray-600 space-y-1">
                      <li>Session management and user authentication</li>
                      <li>Security and fraud prevention</li>
                      <li>Load balancing and website functionality</li>
                      <li>Remembering your cookie preferences</li>
                    </ul>
                  </div>

                  <div className="bg-green-50 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                      <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center mr-3">
                        <i className="ri-bar-chart-line text-white text-sm"></i>
                      </div>
                      Analytics Cookies
                    </h3>
                    <p className="text-gray-600 leading-relaxed mb-3">
                      These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.
                    </p>
                    <ul className="list-disc pl-6 text-gray-600 space-y-1">
                      <li>Google Analytics for website traffic analysis</li>
                      <li>Page views and user behavior tracking</li>
                      <li>Performance monitoring and optimization</li>
                      <li>Error tracking and debugging</li>
                    </ul>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                      <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center mr-3">
                        <i className="ri-user-line text-white text-sm"></i>
                      </div>
                      Functional Cookies
                    </h3>
                    <p className="text-gray-600 leading-relaxed mb-3">
                      These cookies enable enhanced functionality and personalization, such as remembering your preferences and settings.
                    </p>
                    <ul className="list-disc pl-6 text-gray-600 space-y-1">
                      <li>Language and region preferences</li>
                      <li>User interface customizations</li>
                      <li>Form data retention</li>
                      <li>Accessibility settings</li>
                    </ul>
                  </div>

                  <div className="bg-orange-50 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                      <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center mr-3">
                        <i className="ri-megaphone-line text-white text-sm"></i>
                      </div>
                      Marketing Cookies
                    </h3>
                    <p className="text-gray-600 leading-relaxed mb-3">
                      These cookies are used to deliver relevant advertisements and track the effectiveness of our marketing campaigns.
                    </p>
                    <ul className="list-disc pl-6 text-gray-600 space-y-1">
                      <li>Targeted advertising and retargeting</li>
                      <li>Social media integration</li>
                      <li>Campaign performance tracking</li>
                      <li>Cross-platform user identification</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Third-Party Cookies */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Third-Party Cookies</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We may use third-party services that set cookies on our website. These services help us provide better functionality and analyze website performance.
                </p>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Google Analytics</h3>
                    <p className="text-gray-600 text-sm mb-3">
                      Helps us understand website traffic and user behavior to improve our services.
                    </p>
                    <a href="https://policies.google.com/privacy" className="text-blue-600 hover:text-blue-700 text-sm cursor-pointer">
                      Google Privacy Policy →
                    </a>
                  </div>
                  
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Social Media Platforms</h3>
                    <p className="text-gray-600 text-sm mb-3">
                      LinkedIn, Twitter, and Facebook cookies for social sharing and integration features.
                    </p>
                    <div className="text-sm space-y-1">
                      <a href="https://www.linkedin.com/legal/privacy-policy" className="text-blue-600 hover:text-blue-700 cursor-pointer block">
                        LinkedIn Privacy →
                      </a>
                      <a href="https://twitter.com/privacy" className="text-blue-600 hover:text-blue-700 cursor-pointer block">
                        Twitter Privacy →
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cookie Duration */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Cookie Duration</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Cookies have different lifespans depending on their purpose:
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-1">
                      <i className="ri-time-line text-blue-600 text-sm"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Session Cookies</h3>
                      <p className="text-gray-600 text-sm">Temporary cookies that are deleted when you close your browser.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mt-1">
                      <i className="ri-calendar-line text-green-600 text-sm"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Persistent Cookies</h3>
                      <p className="text-gray-600 text-sm">Remain on your device for a set period (typically 1-24 months) or until manually deleted.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Managing Cookies */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Managing Your Cookie Preferences</h2>
                <p className="text-gray-600 leading-relaxed mb-6">
                  You have several options for managing cookies on our website:
                </p>

                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Browser Settings</h3>
                  <p className="text-gray-600 mb-4">
                    Most web browsers allow you to control cookies through their settings. You can:
                  </p>
                  <ul className="list-disc pl-6 text-gray-600 space-y-2">
                    <li>Block all cookies</li>
                    <li>Block third-party cookies only</li>
                    <li>Delete existing cookies</li>
                    <li>Set up notifications when cookies are being sent</li>
                  </ul>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Chrome</h4>
                    <p className="text-gray-600 text-sm">Settings → Privacy and Security → Cookies and other site data</p>
                  </div>
                  
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Firefox</h4>
                    <p className="text-gray-600 text-sm">Options → Privacy & Security → Cookies and Site Data</p>
                  </div>
                  
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Safari</h4>
                    <p className="text-gray-600 text-sm">Preferences → Privacy → Manage Website Data</p>
                  </div>
                  
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Edge</h4>
                    <p className="text-gray-600 text-sm">Settings → Cookies and site permissions → Cookies and site data</p>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <i className="ri-information-line text-yellow-600 mt-1"></i>
                    <div>
                      <h4 className="font-semibold text-yellow-800">Important Note</h4>
                      <p className="text-yellow-700 text-sm">
                        Disabling cookies may affect the functionality of our website and limit your access to certain features.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Do Not Track */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Do Not Track Signals</h2>
                <p className="text-gray-600 leading-relaxed">
                  Some browsers include a "Do Not Track" feature that lets you tell websites you do not want to have your online activities tracked. Currently, there is no standard way for websites to respond to these signals, but we respect your privacy choices and are committed to providing transparency about our data practices.
                </p>
              </div>

              {/* Updates */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Updates to This Cookie Policy</h2>
                <p className="text-gray-600 leading-relaxed">
                  We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the updated policy on our website and updating the "Last updated" date.
                </p>
              </div>

              {/* Contact Information */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Us</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  If you have any questions about our use of cookies or this Cookie Policy, please contact us:
                </p>
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <i className="ri-mail-line text-blue-600 mr-3"></i>
                      <span className="text-gray-700">Email: </span>
                      <a href="mailto:privacy@wotech.io" className="text-blue-600 hover:text-blue-700 cursor-pointer ml-1">
                        privacy@wotech.io
                      </a>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-map-pin-line text-blue-600 mr-3"></i>
                      <span className="text-gray-700">Address: WOTECH Information Technologies, Delaware, USA</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-global-line text-blue-600 mr-3"></i>
                      <span className="text-gray-700">Website: </span>
                      <a href="https://wotech.io" className="text-blue-600 hover:text-blue-700 cursor-pointer ml-1">
                        https://wotech.io
                      </a>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-600">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Questions About Cookies?</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              We're here to help you understand how we use cookies and how you can manage your preferences.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="mailto:privacy@wotech.io" className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
                Contact Us
              </a>
              <a href="/privacy" className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
                Privacy Policy
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default CookiePage;
